/**
 * CozyPageWrapper.jsx
 * ─────────────────────────────────────────────────────────────────
 * Wrapper de transition de page pour ColoBongo.
 * Enveloppe le contenu de chaque page avec une animation d'entrée
 * douce et une sortie propre.
 *
 * Usage dans App.js — entourer chaque <Route> avec ce wrapper :
 *
 *   import CozyPageWrapper from './components/CozyPageWrapper';
 *
 *   // Dans chaque page :
 *   export default function GalleryPage({ t }) {
 *     return (
 *       <CozyPageWrapper>
 *         ...contenu...
 *       </CozyPageWrapper>
 *     );
 *   }
 *
 * Ou directement dans App.js autour des routes :
 *
 *   <AnimatePresence mode="wait">
 *     <Routes location={location} key={location.pathname}>
 *       <Route path="/gallery" element={<CozyPageWrapper><GalleryPage /></CozyPageWrapper>} />
 *     </Routes>
 *   </AnimatePresence>
 * ─────────────────────────────────────────────────────────────────
 */

import React from 'react';
import { motion } from 'framer-motion';
import { useCozyAnimation } from '../hooks/useCozyAnimation';
import { useSectionTheme } from '../hooks/useSectionTheme';

export default function CozyPageWrapper({ children, className = '' }) {
  const { variants } = useCozyAnimation();
  const sectionTheme = useSectionTheme();

  return (
    <motion.div
      variants={variants.pageEnter}
      initial="hidden"
      animate="show"
      exit="exit"
      className={className}
      style={{
        position: 'relative',
        background: sectionTheme.tint,
        transition: 'background 280ms var(--cb-ease-out)',
      }}
    >
      {/* Liseré 3px en haut - couleur de section, change avec la route.
          Les modals/toasts sont en portals (document.body) et ne sont pas
          enfants de ce wrapper - ils ne sont donc PAS teintes. */}
      <div
        aria-hidden="true"
        style={{
          height: 3,
          width: '100%',
          background: sectionTheme.accent,
          transition: 'background 280ms var(--cb-ease-out)',
          flexShrink: 0,
        }}
      />
      {children}
    </motion.div>
  );
}
